import { FormEvent, useState } from "react";
import { inventoryApi, inventoryCategoryApi } from "../api";
import { InventoryCategory, InventoryItem, InventoryItemCreateRequest } from "../types";
import { useAsyncData } from "../hooks";

export function InventoryItemForm({
  initial,
  submitting,
  onSubmit,
}: {
  initial?: InventoryItem | null;
  submitting?: boolean;
  onSubmit: (payload: InventoryItemCreateRequest) => Promise<void> | void;
}) {
  const [form, setForm] = useState<InventoryItemCreateRequest>({
    categoryId: initial?.categoryId ?? null,
    code: initial?.code ?? "",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    itemType: initial?.itemType ?? "CONSUMABLE",
    stockUnitName: initial?.stockUnitName ?? "",
    issueUnitName: initial?.issueUnitName ?? "",
    conversionFactor: initial?.conversionFactor ?? 1,
    currentStockInStockUnits: initial?.currentStockInStockUnits ?? 0,
    reorderLevelInIssueUnits: initial?.reorderLevelInIssueUnits ?? 0,
    maximumLevelInIssueUnits: initial?.maximumLevelInIssueUnits ?? null,
    status: initial?.status ?? "ACTIVE",
  });

  const { data: categories } = useAsyncData<InventoryCategory[]>(
    () => inventoryCategoryApi.list(),
    [],
  );

  const [suggestingCode, setSuggestingCode] = useState(false);
  const selectedCategory = (categories ?? []).find((c) => c.id === form.categoryId) ?? null;

  function set<K extends keyof InventoryItemCreateRequest>(key: K, value: InventoryItemCreateRequest[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  // Category defines a reusable classification code (e.g. STAT for Stationery); Item Code must
  // still be unique per item, so picking a category only *suggests* a code by extending the
  // category's code with a running number — it never overwrites a code the user already typed.
  async function handleCategoryChange(value: string) {
    const id = value ? Number(value) : null;
    set("categoryId", id);
    if (!id || form.code.trim() !== "") return;
    const cat = (categories ?? []).find((c) => c.id === id);
    if (!cat) return;
    setSuggestingCode(true);
    try {
      const existing = await inventoryApi.listItems({ categoryId: id, page: 0, size: 1 });
      const seq = existing.totalElements + 1;
      set("code", `${cat.code}-${String(seq).padStart(3, "0")}`);
    } catch {
      // Best-effort suggestion only — leave the field blank for manual entry if this fails.
    } finally {
      setSuggestingCode(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onSubmit(form);
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-2xl border bg-white p-5 shadow-sm md:grid-cols-2">
      <Field label="Category">
        <select
          className="input"
          value={form.categoryId ?? ""}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          <option value="">— No category —</option>
          {(categories ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
          ))}
        </select>
        {selectedCategory?.description && (
          <p className="text-xs text-slate-400">{selectedCategory.description}</p>
        )}
      </Field>

      <Field label="Item Code" required>
        <input className="input" value={form.code} onChange={(e) => set("code", e.target.value)} required />
        {suggestingCode && (
          <p className="text-xs text-slate-400">Suggesting a code from the selected category…</p>
        )}
      </Field>

      <Field label="Item Name" required>
        <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
      </Field>

      <Field label="Item Type">
        <select className="input" value={form.itemType ?? "CONSUMABLE"} onChange={(e) => set("itemType", e.target.value as InventoryItemCreateRequest["itemType"])}>
          <option value="CONSUMABLE">Consumable (goes to stock on GRN)</option>
          <option value="CAPITAL_ASSET">Capital Asset (creates asset record on GRN)</option>
          <option value="SERVICE">Service (no stock action)</option>
          <option value="WORKS">Works (no stock action)</option>
        </select>
      </Field>

      <Field label="Status">
        <select className="input" value={form.status} onChange={(e) => set("status", e.target.value)}>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </Field>

      <Field label="Stock Unit" required>
        <input className="input" placeholder="e.g. Carton" value={form.stockUnitName} onChange={(e) => set("stockUnitName", e.target.value)} required />
      </Field>

      <Field label="Issue Unit" required>
        <input className="input" placeholder="e.g. Piece" value={form.issueUnitName} onChange={(e) => set("issueUnitName", e.target.value)} required />
      </Field>

      <Field label="Conversion Factor" required>
        <input className="input" type="number" min="0.0001" step="0.0001" value={form.conversionFactor}
          onChange={(e) => set("conversionFactor", Number(e.target.value))} required />
        <p className="text-xs text-slate-400">How many issue units per stock unit (e.g. 1 carton = 12 pieces → enter 12)</p>
      </Field>

      <Field label="Opening Stock (stock units)">
        <input className="input" type="number" min="0" step="0.0001"
          value={form.currentStockInStockUnits ?? 0}
          onChange={(e) => set("currentStockInStockUnits", Number(e.target.value))} />
      </Field>

      <Field label="Reorder Level (issue units)">
        <input className="input" type="number" min="0" step="0.0001"
          value={form.reorderLevelInIssueUnits ?? 0}
          onChange={(e) => set("reorderLevelInIssueUnits", Number(e.target.value))} />
      </Field>

      <Field label="Maximum Level (issue units)">
        <input className="input" type="number" min="0" step="0.0001"
          value={form.maximumLevelInIssueUnits ?? ""}
          onChange={(e) => set("maximumLevelInIssueUnits", e.target.value ? Number(e.target.value) : null)} />
      </Field>

      <div className="md:col-span-2">
        <Field label="Description">
          <textarea className="input min-h-20" value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} />
        </Field>
      </div>

      <div className="md:col-span-2 flex justify-end">
        <button disabled={submitting}
          className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
          {submitting ? "Saving..." : "Save Item"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="space-y-1.5 text-sm font-medium text-slate-700">
      <span>{label}{required && <span className="ml-1 text-red-500">*</span>}</span>
      {children}
    </label>
  );
}
